/**
 * F27 — WhatsApp Business API (Meta Cloud API direct) shared types + Zod schemas.
 *
 * Surfaces:
 *   - WhatsAppTemplateStatus, WhatsAppTemplateCategory: the Meta-canonical
 *     enums persisted on `whatsapp_templates`
 *   - WhatsAppTemplateName: closed union of the 5 templates F27 ships at MVP
 *     (Decision D5 — otpLogin, bookingConfirmation, bookingReminder1h,
 *     paymentReceipt, paymentMarked). camelCase to match the existing
 *     `packages/i18n/src/whatsapp-templates.ts` registry convention
 *     (per Phase 0 V5 finding / §3 A1 amendment).
 *   - WhatsAppQualityRating, WhatsAppMessagingLimitTier,
 *     WhatsAppPhoneNumberStatus: Meta-returned values on the daily quality
 *     poll, persisted on `whatsapp_quality_state` (Decision D11)
 *   - WhatsAppProviderMode: 'fake' (dev/test — writes JSON to tmpdir) or
 *     'meta_cloud_api' (prod — calls Meta Graph API). Mirrors F26's
 *     FakeFcmClient + F28's FakePostmarkClient (Decision D1)
 *   - WhatsAppTemplate, WhatsAppQualityState, WhatsAppOptInStatus: read shapes
 *     returned by routes
 *   - WhatsAppErrorCode: 5 codes used across the WhatsAppChannel + routes
 *   - Input schemas: OptIn POST + Webhook payload (Meta-canonical shape)
 *
 * Cross-references:
 *   - F26 NotificationChannel ('whatsapp' is one of its 4 values; reserved at
 *     F26 initial shipment)
 *   - F26 NotificationCategory ('system' for verification.received OTP routes;
 *     'booking' for booking.accepted + booking.reminder_1h; 'payment' for
 *     payment.marked_paid + payment.marked_refunded)
 *   - apps/api/src/services/notification-channels/whatsapp.ts consumes these
 *   - apps/api/src/services/whatsapp-provider.ts defines the WhatsAppProvider
 *     interface and FakeWhatsAppProvider impl
 *   - apps/api/src/services/whatsapp-template-registry.ts wraps the
 *     `whatsapp_templates` table reads (cache-first 5-min TTL)
 */

import { z } from 'zod';

// ─── Provider mode (Decision D1) ────────────────────────────────────────────

export const WHATSAPP_PROVIDER_MODES = ['fake', 'meta_cloud_api'] as const;

export type WhatsAppProviderMode = (typeof WHATSAPP_PROVIDER_MODES)[number];

export const WhatsAppProviderModeSchema = z.enum(WHATSAPP_PROVIDER_MODES);

// ─── Template registry (Decision D14, D5) ───────────────────────────────────

/**
 * Meta-canonical template lifecycle (Decision D14). The
 * `WhatsAppTemplateRegistryService.getApprovedTemplate(name, language)` returns
 * null for any status other than 'APPROVED' — PAUSED, REJECTED, DISABLED,
 * PENDING, IN_APPEAL all trigger the email-fallback path (Decision D7).
 */
export const WHATSAPP_TEMPLATE_STATUSES = [
  'PENDING',
  'APPROVED',
  'REJECTED',
  'PAUSED',
  'DISABLED',
  'IN_APPEAL',
] as const;

export type WhatsAppTemplateStatus = (typeof WHATSAPP_TEMPLATE_STATUSES)[number];

export const WhatsAppTemplateStatusSchema = z.enum(WHATSAPP_TEMPLATE_STATUSES);

/**
 * Meta-canonical template category. MARKETING is in the enum for completeness
 * but no MARKETING-category templates are submitted at MVP (Decision D5).
 */
export const WHATSAPP_TEMPLATE_CATEGORIES = ['AUTHENTICATION', 'UTILITY', 'MARKETING'] as const;

export type WhatsAppTemplateCategory = (typeof WHATSAPP_TEMPLATE_CATEGORIES)[number];

export const WhatsAppTemplateCategorySchema = z.enum(WHATSAPP_TEMPLATE_CATEGORIES);

/**
 * Closed union of the 5 templates F27 ships at MVP (Decision D5).
 *
 * camelCase keys match the existing `packages/i18n/src/whatsapp-templates.ts`
 * registry (Phase 0 V5 / §3 A1 amendment). `otpLogin` + `bookingReminder1h`
 * are NEW keys added in Wave 1 Task 1.9; the other three are already-existing
 * keys whose registry rows flip to `enabled: true` once Meta approves them.
 */
export const WHATSAPP_F27_TEMPLATE_NAMES = [
  'otpLogin',
  'bookingConfirmation',
  'bookingReminder1h',
  'paymentReceipt',
  'paymentMarked',
] as const;

export type WhatsAppTemplateName = (typeof WHATSAPP_F27_TEMPLATE_NAMES)[number];

export const WhatsAppTemplateNameSchema = z.enum(WHATSAPP_F27_TEMPLATE_NAMES);

/** Mapping `WhatsAppTemplateName → WhatsAppTemplateCategory` (Decision D5). */
export const WHATSAPP_TEMPLATE_NAME_TO_CATEGORY: Readonly<
  Record<WhatsAppTemplateName, WhatsAppTemplateCategory>
> = Object.freeze({
  otpLogin: 'AUTHENTICATION',
  bookingConfirmation: 'UTILITY',
  bookingReminder1h: 'UTILITY',
  paymentReceipt: 'UTILITY',
  paymentMarked: 'UTILITY',
});

/** Read shape returned by `GET /admin/whatsapp/templates`. */
export interface WhatsAppTemplate {
  readonly id: string;
  readonly name: string;
  readonly language: string;
  readonly category: WhatsAppTemplateCategory;
  readonly status: WhatsAppTemplateStatus;
  readonly metaTemplateId: string | null;
  readonly rejectionReason: string | null;
  readonly lastSyncedAt: string; // ISO 8601
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

// ─── Quality state (Decision D11) ───────────────────────────────────────────

export const WHATSAPP_QUALITY_RATINGS = ['GREEN', 'YELLOW', 'RED', 'UNKNOWN'] as const;

export type WhatsAppQualityRating = (typeof WHATSAPP_QUALITY_RATINGS)[number];

export const WhatsAppQualityRatingSchema = z.enum(WHATSAPP_QUALITY_RATINGS);

export const WHATSAPP_MESSAGING_LIMIT_TIERS = [
  'TIER_50',
  'TIER_250',
  'TIER_1K',
  'TIER_10K',
  'TIER_100K',
  'TIER_UNLIMITED',
] as const;

export type WhatsAppMessagingLimitTier = (typeof WHATSAPP_MESSAGING_LIMIT_TIERS)[number];

export const WhatsAppMessagingLimitTierSchema = z.enum(WHATSAPP_MESSAGING_LIMIT_TIERS);

export const WHATSAPP_PHONE_NUMBER_STATUSES = [
  'CONNECTED',
  'FLAGGED',
  'RESTRICTED',
  'PENDING_REVIEW',
  'DISCONNECTED',
] as const;

export type WhatsAppPhoneNumberStatus = (typeof WHATSAPP_PHONE_NUMBER_STATUSES)[number];

export const WhatsAppPhoneNumberStatusSchema = z.enum(WHATSAPP_PHONE_NUMBER_STATUSES);

/** Read shape for `GET /admin/whatsapp/quality`. */
export interface WhatsAppQualityState {
  readonly phoneNumberId: string;
  readonly displayPhoneNumber: string; // E.164
  readonly verifiedName: string | null;
  readonly qualityRating: WhatsAppQualityRating | null;
  readonly messagingLimitTier: WhatsAppMessagingLimitTier | null;
  readonly status: WhatsAppPhoneNumberStatus;
  readonly lastSyncedAt: string; // ISO 8601
}

// ─── Opt-in state (Decision D3) ─────────────────────────────────────────────

/** Read shape returned by `GET /me/whatsapp/opt-in-status`. */
export interface WhatsAppOptInStatus {
  readonly optedIn: boolean;
  readonly phone: string | null; // E.164 or null
  readonly optedInAt: string | null; // ISO 8601 or null
  readonly optedOutAt: string | null; // ISO 8601 or null
}

/**
 * Input schema for `POST /me/whatsapp/opt-in`. Phone is validated server-side
 * via `parsePhone(phone, 'LB')` from `apps/api/src/lib/phone.ts` (Phase 0 V4
 * confirmed the helper exists). Raw shape — service rejects with PHONE_INVALID
 * if libphonenumber-js can't parse it.
 */
export const OptInWhatsAppInputSchema = z.object({
  phone: z.string().min(1, 'phone is required'),
});

export type OptInWhatsAppInput = z.infer<typeof OptInWhatsAppInputSchema>;

// ─── Error codes ────────────────────────────────────────────────────────────

export const WHATSAPP_ERROR_CODES = [
  'PHONE_INVALID', // 422 — libphonenumber-js parse failed OR not E.164
  'NOT_OPTED_IN', // suppressed_by_preference — no opt-in on file
  'TEMPLATE_NOT_APPROVED', // suppressed_by_preference — template status ≠ APPROVED
  'RATE_LIMITED', // pending_retry — Meta returned 131048 (per-number tier limit)
  'PROVIDER_TRANSIENT_ERROR', // pending_retry — Meta returned 5xx / network failure
] as const;

export type WhatsAppErrorCode = (typeof WHATSAPP_ERROR_CODES)[number];

// ─── Provider interface (cross-package contract; impl in apps/api) ──────────

/**
 * Variables passed to `WhatsAppProvider.sendTemplate`. Meta accepts up to 10
 * positional `{{1}}`, `{{2}}`, ... vars per template — the per-template render
 * functions in `apps/api/src/services/whatsapp-template-render.ts` (Wave 2B)
 * produce the right shape per template's variable schema.
 */
export interface WhatsAppTemplateComponent {
  readonly type: 'header' | 'body' | 'button';
  readonly subType?: 'quick_reply' | 'url' | 'phone_number' | 'copy_code';
  readonly index?: number; // button index (0-2)
  readonly parameters: ReadonlyArray<WhatsAppTemplateParameter>;
}

export interface WhatsAppTemplateParameter {
  readonly type: 'text' | 'currency' | 'date_time' | 'image' | 'document' | 'video';
  readonly text?: string;
}

/**
 * Provider contract (Decision D1). The interface is `WhatsAppProvider`; the
 * impls are `FakeWhatsAppProvider` (dev/test) and `DefaultMetaCloudApiProvider`
 * (prod — Wave 2A). The factory `createWhatsAppProvider(env)` selects based
 * on `env.WHATSAPP_PROVIDER_MODE`.
 *
 * `sendTemplate` returns the Meta `messages[0].id` for webhook correlation.
 * `listTemplates` is called by `WhatsAppTemplateSyncWorker` (Wave 2C).
 * `getPhoneNumberStatus` is called by `WhatsAppQualityMonitorWorker` (Wave 2C).
 */
export interface WhatsAppProvider {
  sendTemplate(input: WhatsAppSendTemplateInput): Promise<WhatsAppSendTemplateResult>;
  listTemplates(): Promise<ReadonlyArray<WhatsAppRemoteTemplate>>;
  getPhoneNumberStatus(): Promise<WhatsAppRemotePhoneStatus>;
}

export interface WhatsAppSendTemplateInput {
  readonly to: string; // E.164
  readonly templateName: WhatsAppTemplateName;
  readonly language: string;
  readonly components: ReadonlyArray<WhatsAppTemplateComponent>;
  /** Future-proofing for multi-number routing (Decision D10). MVP = single number. */
  readonly senderPhoneNumberId?: string;
}

export interface WhatsAppSendTemplateResult {
  readonly messageId: string; // Meta's messages[0].id
}

/** What Meta's `GET /{waba-id}/message_templates` returns per template. */
export interface WhatsAppRemoteTemplate {
  readonly id: string;
  readonly name: string;
  readonly language: string;
  readonly category: WhatsAppTemplateCategory;
  readonly status: WhatsAppTemplateStatus;
  readonly rejectionReason?: string;
}

/** What Meta's `GET /{phone-number-id}?fields=...` returns. */
export interface WhatsAppRemotePhoneStatus {
  readonly id: string;
  readonly displayPhoneNumber: string;
  readonly verifiedName: string | null;
  readonly qualityRating: WhatsAppQualityRating | null;
  readonly messagingLimitTier: WhatsAppMessagingLimitTier | null;
  readonly status: WhatsAppPhoneNumberStatus;
}

// ─── Webhook payload (Decision D9) ──────────────────────────────────────────

/**
 * Meta WhatsApp Business webhook envelope. The full Meta payload is much
 * larger; this schema models only the fields F27 reads (statuses[].id +
 * statuses[].status for delivery callbacks, messages[] for inbound dropouts).
 *
 * Inbound `messages[]` events are LOGGED to pino (`whatsapp.event=
 * inbound_dropped`) per Decision D6 — no service-window handling at MVP.
 */
export const WhatsAppWebhookStatusSchema = z.object({
  id: z.string(), // Meta's messages[0].id from the original send
  status: z.enum(['sent', 'delivered', 'read', 'failed']),
  timestamp: z.string(), // Unix epoch as string
  recipient_id: z.string().optional(),
  errors: z
    .array(
      z.object({
        code: z.number(),
        title: z.string().optional(),
        message: z.string().optional(),
      }),
    )
    .optional(),
});

export type WhatsAppWebhookStatus = z.infer<typeof WhatsAppWebhookStatusSchema>;

export const WhatsAppWebhookValueSchema = z.object({
  messaging_product: z.literal('whatsapp'),
  metadata: z
    .object({
      display_phone_number: z.string(),
      phone_number_id: z.string(),
    })
    .optional(),
  statuses: z.array(WhatsAppWebhookStatusSchema).optional(),
  messages: z.array(z.unknown()).optional(), // dropped per D6, not modelled
});

export const WhatsAppWebhookChangeSchema = z.object({
  field: z.string(),
  value: WhatsAppWebhookValueSchema,
});

export const WhatsAppWebhookEntrySchema = z.object({
  id: z.string(), // WABA id
  changes: z.array(WhatsAppWebhookChangeSchema),
});

export const WhatsAppWebhookPayloadSchema = z.object({
  object: z.literal('whatsapp_business_account'),
  entry: z.array(WhatsAppWebhookEntrySchema),
});

export type WhatsAppWebhookPayload = z.infer<typeof WhatsAppWebhookPayloadSchema>;
